import { Service, inject } from '@angular/core'
import { TaskQuery } from '@awork/features/task/state/task.query'
import replace from '@awork/_shared/functions/replace'
import type { UndoCaptureMetadata } from './types'

/** Describes a recorded change using task data already in the store. */
@Service()
export class UndoLabelService {
  private readonly taskQuery = inject(TaskQuery)

  /**
   * Names the recorded field and affected tasks without another API request.
   * @param {UndoCaptureMetadata} entry - The server metadata.
   * @returns {string} The action description without its Undo or Redo prefix.
   */
  build(entry: UndoCaptureMetadata): string {
    const translations = q.translations.UndoService.label
    const labels = {
      status: [translations.statusOne, translations.statusMany],
      priority: [translations.priorityOne, translations.priorityMany],
      dates: [translations.datesOne, translations.datesMany],
      lane: [translations.laneOne, translations.laneMany],
      assignees: [translations.assigneesOne, translations.assigneesMany],
      changes: [translations.changesOne, translations.changesMany]
    }
    let field: keyof typeof labels = 'changes'

    if (entry.fields.length === 1 && Object.hasOwn(labels, entry.fields[0])) {
      field = entry.fields[0] as keyof typeof labels
    }

    if (entry.count > 1) {
      return replace(labels[field][1], { n: String(entry.count) })
    }

    const name = entry.taskId ? this.taskQuery.getEntity(entry.taskId)?.name : undefined

    if (!name) {
      return translations.generic
    }

    const task = name.length > 40 ? `${name.slice(0, 40)}…` : name

    return replace(labels[field][0], { task })
  }
}
